import type { APIMessage } from '@alternativamais/chat-core';
import type { ReactNode } from 'react';

import { Avatar } from '../../componentes/Avatar';
import { Icone } from '../../componentes/Icone';
import { Markdown } from '../../componentes/Markdown';
import { NomeDoAutor } from './mensagem/Mensagem';

/** Caixa dos popovers do cabeçalho (fixadas, threads, caixa de entrada). */
export function CaixaPopover({
  titulo,
  icone,
  largura = 440,
  altura = 560,
  aoFechar,
  acoes,
  children,
  rodape,
}: {
  titulo: string;
  icone?: Parameters<typeof Icone>[0]['nome'];
  largura?: number;
  altura?: number;
  aoFechar: () => void;
  acoes?: ReactNode;
  children: ReactNode;
  rodape?: ReactNode;
}) {
  return (
    <div
      className="flex flex-col overflow-hidden rounded-xl border border-borda bg-elevado shadow-[0_12px_32px_rgba(0,0,0,.45)]"
      style={{
        width: largura,
        maxWidth: 'calc(100vw - 16px)',
        height: altura,
        maxHeight: 'calc(100vh - 80px)',
      }}
    >
      <div className="flex h-12 shrink-0 items-center gap-2.5 border-b border-borda pr-2 pl-4">
        {icone && <Icone nome={icone} tamanho={20} className="text-texto-2" />}
        <h2 className="m-0 grow truncate text-base font-semibold">{titulo}</h2>
        {acoes}
        <button
          type="button"
          aria-label="Fechar"
          onClick={aoFechar}
          className="flex size-8 items-center justify-center rounded-md text-texto-3 hover:text-texto"
        >
          <Icone nome="x" tamanho={18} />
        </button>
      </div>
      <div className="rolagem min-h-0 grow overflow-y-auto">{children}</div>
      {rodape}
    </div>
  );
}

const DIA_CURTO = new Intl.DateTimeFormat('pt-BR', {
  day: 'numeric',
  month: 'short',
});
const DIA_E_ANO = new Intl.DateTimeFormat('pt-BR', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/** "12 de set." (ou com o ano, se não é deste ano). */
export function diaCurto(quando: string) {
  const data = new Date(quando);
  return (
    data.getFullYear() === new Date().getFullYear() ? DIA_CURTO : DIA_E_ANO
  ).format(data);
}

/**
 * Mensagem resumida (fixadas, busca, caixa de entrada): avatar, nome, data,
 * o texto e os anexos; à direita, as ações (Ir, desafixar…).
 */
export function MensagemResumida({
  mensagem,
  guildId,
  acoes,
  destacar,
  data,
}: {
  mensagem: APIMessage;
  guildId: string | null;
  acoes?: ReactNode;
  /** Termos da busca para marcar. */
  destacar?: string[];
  data?: string;
}) {
  return (
    <article className="group relative flex gap-3 rounded-lg border border-borda bg-chat p-3">
      <Avatar
        pessoa={mensagem.author}
        nome={mensagem.author.global_name ?? mensagem.author.username}
        tamanho={36}
      />
      <div className="flex min-w-0 grow flex-col gap-0.5">
        <div className="flex min-w-0 items-baseline gap-2">
          <NomeDoAutor autor={mensagem.author} guildId={guildId} />
          <time className="shrink-0 text-xs text-texto-3">
            {data ?? diaCurto(mensagem.timestamp)}
          </time>
        </div>
        {mensagem.content && (
          <div className="text-[15px] leading-[1.45] [&_mark]:rounded-sm [&_mark]:bg-destaque-suave [&_mark]:text-destaque-claro">
            {destacar?.length ? (
              <TextoMarcado texto={mensagem.content} termos={destacar} />
            ) : (
              <Markdown
                texto={mensagem.content}
                guildId={guildId}
                className="line-clamp-6"
              />
            )}
          </div>
        )}
        {mensagem.attachments.map((anexo) => (
          <span
            key={anexo.id}
            className="mt-1 flex items-center gap-1.5 text-[13px] text-texto-3"
          >
            <Icone
              nome={
                anexo.content_type?.startsWith('image/') ? 'imagem' : 'clipe'
              }
              tamanho={14}
            />
            <span className="truncate">{anexo.filename}</span>
          </span>
        ))}
        {!mensagem.content && mensagem.embeds[0]?.title && (
          <span className="text-sm text-texto-2">
            {mensagem.embeds[0].title}
          </span>
        )}
      </div>
      {acoes && (
        <div className="absolute top-2 right-2 hidden items-center gap-1 group-focus-within:flex group-hover:flex">
          {acoes}
        </div>
      )}
    </article>
  );
}

/** Texto com os termos da busca marcados (sem acento, sem maiúsculas). */
function TextoMarcado({ texto, termos }: { texto: string; termos: string[] }) {
  const normal = (valor: string) =>
    valor
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();
  const base = normal(texto);
  const marcas: Array<[number, number]> = [];
  for (const termo of termos.map(normal).filter((item) => item.length > 1)) {
    let posicao = base.indexOf(termo);
    while (posicao >= 0) {
      marcas.push([posicao, posicao + termo.length]);
      posicao = base.indexOf(termo, posicao + termo.length);
    }
  }
  marcas.sort((a, b) => a[0] - b[0]);
  const pedacos: ReactNode[] = [];
  let cursor = 0;
  for (const [inicio, fim] of marcas) {
    if (inicio < cursor) continue;
    pedacos.push(texto.slice(cursor, inicio));
    pedacos.push(<mark key={inicio}>{texto.slice(inicio, fim)}</mark>);
    cursor = fim;
  }
  pedacos.push(texto.slice(cursor));
  return (
    <p className="m-0 line-clamp-6 [overflow-wrap:anywhere] whitespace-pre-wrap">
      {pedacos}
    </p>
  );
}

export function BotaoIr({
  aoClicar,
  rotulo = 'Ir para a mensagem',
}: {
  aoClicar: () => void;
  rotulo?: string;
}) {
  return (
    <button
      type="button"
      aria-label={rotulo}
      onClick={aoClicar}
      className="h-6 rounded-md bg-ativo px-2 text-xs font-semibold text-texto hover:brightness-110"
    >
      Ir
    </button>
  );
}

export function Carregando({ texto = 'Carregando…' }: { texto?: string }) {
  return (
    <p className="m-0 px-4 py-6 text-center text-sm text-texto-3">{texto}</p>
  );
}

export function Vazio({
  icone,
  titulo,
  texto,
}: {
  icone: Parameters<typeof Icone>[0]['nome'];
  titulo: string;
  texto?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
      <span className="flex size-14 items-center justify-center rounded-full bg-ativo text-texto-2">
        <Icone nome={icone} tamanho={28} />
      </span>
      <p className="m-0 text-[15px] font-semibold">{titulo}</p>
      {texto && <p className="m-0 text-sm text-texto-3">{texto}</p>}
    </div>
  );
}
